export const SEVERITY_ORDER = { mild: 1, moderate: 2, severe: 3, very_severe: 4 };

export const SEVERITY_LABELS = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
  very_severe: 'Very Severe',
};

export const SEVERITY_COLORS = {
  mild: '#338477',
  moderate: '#EF9F27',
  severe: '#E24B4A',
  very_severe: '#9B1C1C',
};

export const SEVERITY_BG = {
  mild: '#E3F0EE',
  moderate: '#FAEEDA',
  severe: '#FCEBEB',
  very_severe: '#FEE2E2',
};

/**
 * Filter symptoms to the selected time range.
 * 'daily'   → last 24 hours
 * 'weekly'  → last 7 days
 * 'monthly' → last 30 days
 */
export function filterByRange(symptoms, range) {
  const cut = new Date();
  if (range === 'daily') {
    cut.setHours(cut.getHours() - 24);
  } else if (range === 'weekly') {
    cut.setDate(cut.getDate() - 7);
    cut.setHours(0, 0, 0, 0);
  } else {
    cut.setDate(cut.getDate() - 30);
    cut.setHours(0, 0, 0, 0);
  }
  return symptoms.filter(s => new Date(s.loggedAt) >= cut);
}

/**
 * Rule-based management recommendations derived from the last 7 days of data.
 */
export function getRecommendations(symptoms) {
  const week = filterByRange(symptoms, 'weekly');
  const recs = [];

  if (week.some(s => s.severity === 'very_severe')) {
    recs.push({
      level: 'urgent',
      icon: '🚨',
      title: 'Contact your care team today',
      text: 'You have logged very severe symptoms recently. Please contact your oncology care team or go to the emergency department right away.',
    });
  }

  if (week.some(s => s.severity === 'severe')) {
    recs.push({
      level: 'warning',
      icon: '⚠️',
      title: 'Severe symptoms detected',
      text: 'Severe symptoms have been logged this week. Consider calling your oncology nurse line to discuss next steps.',
    });
  }

  const modCount = week.filter(s => s.severity === 'moderate').length;
  if (modCount >= 3) {
    recs.push({
      level: 'info',
      icon: '📋',
      title: 'Recurring moderate symptoms',
      text: `You've logged ${modCount} moderate symptoms this week. Mention these at your next appointment so your care team can review your treatment plan.`,
    });
  }

  if (week.length >= 2) {
    recs.push({
      level: 'tip',
      icon: '💧',
      title: 'Stay hydrated',
      text: 'Good hydration can help manage many treatment side effects including fatigue, nausea, and constipation.',
    });
  }

  recs.push({
    level: 'tip',
    icon: '💡',
    title: 'Consistent logging helps',
    text: 'Regular symptom logging helps your care team spot patterns and fine-tune your treatment plan over time.',
  });

  return recs;
}

/**
 * Produces a plain-text summary of recent symptoms suitable for inclusion
 * in the Reaxano AI chatbot system prompt or user message.
 *
 * Usage (future integration):
 *   const context = summarizeForChatbot(symptoms);
 *   // prepend `context` to the user's message when calling /api/chat
 */
export function summarizeForChatbot(symptoms) {
  if (!symptoms || symptoms.length === 0) return '';

  const recent = [...symptoms]
    .sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt))
    .slice(0, 10);

  const lines = recent.map(s => {
    const d = new Date(s.loggedAt).toLocaleDateString('en-CA');
    return `- ${s.name} (${SEVERITY_LABELS[s.severity]}) on ${d}${s.note ? ': ' + s.note : ''}`;
  });

  return `Recent tracked symptoms:\n${lines.join('\n')}`;
}
